import { SvgIcon, SvgIconProps } from "@mui/material";

/**
 * The stroke glyphs the design uses on this screen. All inherit `currentColor`
 * and are decorative (`aria-hidden`) — the text beside them carries the name.
 *
 * `fill: none` has to be set through `sx`, not only as the attribute: MUI's
 * SvgIcon puts `fill: currentColor` in its CSS, which beats the attribute and
 * turns every outline into a solid blob.
 */
const stroke = (props: SvgIconProps, strokeWidth = 2) => {
  const { sx, ...rest } = props;
  return {
    viewBox: "0 0 24 24",
    stroke: "currentColor",
    strokeWidth,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    focusable: false,
    ...rest,
    sx: [{ fill: "none" }, ...(Array.isArray(sx) ? sx : [sx ?? {}])],
  };
};

export const SearchGlyph = (props: SvgIconProps) => (
  <SvgIcon {...stroke(props)}>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.6-3.6" />
  </SvgIcon>
);

/** Four-point sparkle — the AI avatar. */
export const SparkGlyph = (props: SvgIconProps) => (
  <SvgIcon {...stroke(props, 2.2)}>
    <path d="M12 3l2.2 5.6L20 10l-5.8 1.4L12 17l-2.2-5.6L4 10l5.8-1.4z" />
  </SvgIcon>
);

export const ArrowRightGlyph = (props: SvgIconProps) => (
  <SvgIcon {...stroke(props, 2.2)}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </SvgIcon>
);

export const ChevronDownGlyph = (props: SvgIconProps) => (
  <SvgIcon {...stroke(props, 2.2)}>
    <path d="M6 9l6 6 6-6" />
  </SvgIcon>
);

export const InfoGlyph = (props: SvgIconProps) => (
  <SvgIcon {...stroke(props, 2.2)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16h.01" />
  </SvgIcon>
);

export const SendGlyph = (props: SvgIconProps) => (
  <SvgIcon {...stroke(props, 2.2)}>
    <path d="M12 19V5M5 12l7-7 7 7" />
  </SvgIcon>
);

export const CloseGlyph = (props: SvgIconProps) => (
  <SvgIcon {...stroke(props, 2.2)}>
    <path d="M6 6l12 12M18 6L6 18" />
  </SvgIcon>
);

/** The design's small "source card" glyph beside the composer note. */
export const DocGlyph = (props: SvgIconProps) => (
  <SvgIcon {...stroke(props)}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9h18" />
  </SvgIcon>
);

/** Stacked-documents glyph for the "contracts indexed" pill (the design's chip icon). */
export const ContractsGlyph = (props: SvgIconProps) => (
  <SvgIcon {...stroke(props)}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 10h18" />
  </SvgIcon>
);

export const PlusGlyph = (props: SvgIconProps) => (
  <SvgIcon {...stroke(props, 2.2)}>
    <path d="M12 5v14M5 12h14" />
  </SvgIcon>
);

export const ExternalGlyph = (props: SvgIconProps) => (
  <SvgIcon {...stroke(props)}>
    <path d="M14 4h6v6M20 4l-9 9" />
    <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
  </SvgIcon>
);
